/**
 * Authentication service — thin wrapper over Firebase Auth.
 * Components/contexts call these instead of importing Firebase directly,
 * so the auth provider can be swapped without touching the UI layer.
 *
 * There is NO public sign-up and NO federated (Google) sign-in: accounts are
 * created only by an admin (see the `adminAccounts` Cloud Function). This
 * service therefore only exposes sign-in / sign-out / password-reset.
 */
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';

import { auth } from './config';

export const authService = {
  /** Subscribe to auth state; returns the unsubscribe fn. */
  observe(callback) {
    return onAuthStateChanged(auth, callback);
  },

  login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  },

  logout() {
    return signOut(auth);
  },

  resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  },

  /**
   * Change the signed-in user's password. Re-authenticates with the current
   * password first — Firebase requires a recent login to change credentials, and
   * this doubles as verification that the current password is correct.
   */
  async changePassword(currentPassword, newPassword) {
    const user = auth.currentUser;
    if (!user || !user.email) {
      const err = new Error('NOT_SIGNED_IN');
      err.code = 'auth/no-current-user';
      throw err;
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  },
};

export default authService;
